import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import * as actionCreator from '../../actions/';
import { useNavigate } from 'react-router';
import MainLayout from '../../layouts/MainLayout';
import { connect } from 'react-redux';
import Overlay from '../../components/Overlay/Overlay';
import languageManager, { T } from '../../helpers/Language';
import CheckMark from 'react-ionicons/lib/CheckmarkCircle';
import CloseCircle from 'react-ionicons/lib/CloseCircle';
import {
    getMyOTPStatus, setupMyOTP,
    removeMyOTP, checkCurrentOTP
} from '../../api/actions/Session';
import OTPCode from '../../components/OTPCode/OTPCode';
import './SettingsHome.scss';

export const TODO_ICO_PROPS = {
    height: '22px',
    width:  '22px',
    color: 'inherit'
};

export const CHECK_ICO_PROPS = {
    height: '30px',
    width:  '30px',
    color: 'inherit'
};

export const AUTH_MOBILE_APPS = [{
    label: 'Google Authenticator',
    iosLink: 'https://apps.apple.com/us/app/google-authenticator/id388497605',
    androidLink: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US'
},{
    label: 'Microsoft Authenticator',
    iosLink: 'https://apps.apple.com/us/app/microsoft-authenticator/id983156458',
    androidLink: 'https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en&gl=US'
}];

const SetupMFAModalBodyAknowledge = ({
    appInstalledStatus, setAppInstalledStatus,
    onNextStep, onClose
}) => (
    <>
        <Modal.Header>
            <Modal.Title>
                Information
                <span className="text-muted"> - Two factor authentication</span>
            </Modal.Title>
        </Modal.Header>

        <Modal.Body>
            <p className="p-2">
                Two factor authentication (2FA) offers enhanced security by providing a single use code sent
                directly to your device at log in.
                This ensures you are the only person who can access your account, even if someone has your password.
                <br/><br/>
                In order to set up two factor authentication, you will need a smartphone with an authenticator app installed, allowing you to scan a QR code to retrieve your code.
            </p>
            <table className="table">
                <thead>
                    <tr>
                        <th colSpan={3}>List of compatible smartphone apps:</th>
                    </tr>
                </thead>
                <tbody>
                    {AUTH_MOBILE_APPS.map((item, idx) => (
                        <tr key={idx}>
                            <td>{item.label}</td>
                            <td><a href={item.androidLink} target="_blank" rel="noopener">Android</a></td>
                            <td><a href={item.iosLink} target="_blank" rel="noopener">iOS</a></td>
                        </tr>  
                    ))}
                </tbody>
            </table>
            <Alert variant="warning">
                <div className="mb-4 fw-bold">
                    The app must be installed on your smartphone prior to proceeding.
                </div>
                <Form.Check
                    type="switch"
                    checked={appInstalledStatus}
                    onChange={(e) => setAppInstalledStatus(e.target.checked)}
                    label={<span className="">By checking this box, you acknowledge you have installed the app and are able to scan the QR Code.</span>}
                />
            </Alert>
        </Modal.Body>

        <Modal.Footer>
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={onNextStep} disabled={!appInstalledStatus}>Get my QR Code</Button>
        </Modal.Footer>
    </>
);

const SetupMFAModalBodyQRCode = ({
    onClose, hasOTPValidated
}) => {
    const [isInit, setInitStatus] = useState(false);
    const [codeMatch, setCodeMatch] = useState(null);
    const [otpSetupRes, setOtpSetupRes] = useState(null);
    const [otpInput, setOtpInput] = useState(null);

    const handleGenerateQRCode = async () => {
        try {
            const res = await setupMyOTP();
            setOtpSetupRes(res);
        } catch (e) {
            console.error(e);
        }
    };

    const handleCheckOTPCode = async () => {
        try {
            setCodeMatch('loading');
            const res = await checkCurrentOTP({ otpCode: otpInput });
            setCodeMatch(res.data ? 'success' : 'failed');
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (isInit) return;
        setInitStatus(true);
        if (!hasOTPValidated) {
            handleGenerateQRCode();
        }
    });
    let codeMatchIcon = null;
    switch (codeMatch) {
        case 'loading':
            // codeMatchIcon = (<span className='spin-anim' style={{fill: 'gray'}}><LoadingIcon {...CHECK_ICO_PROPS} /></span>);
            codeMatchIcon = (
                <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            );
        break;
        case 'success':
            codeMatchIcon = (<span style={{fill: 'green'}}><CheckMark {...CHECK_ICO_PROPS} /></span>);
        break;
        case 'failed':
            codeMatchIcon = (<span style={{fill: 'orange'}}><CloseCircle {...CHECK_ICO_PROPS} /></span>);
        break;
        default:
        break;
    }
    return (
        <>
            <Modal.Header>
                <Modal.Title>
                    QRCode
                    <span className="text-muted"> - Two factor authentication</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Container fluid>
                    <Row>
                        <Col className="text-right">
                            {
                                otpSetupRes && otpSetupRes.imageURL ?
                                <img src={otpSetupRes.imageURL} width="100%" />:
                                'loading'
                            }
                        </Col>
                        <Col>
                            <ul className="list-group">
                                <li className="list-group-item">1) Open your Authenticator app</li>
                                <li className="list-group-item">2) Scan your code</li>
                                <li className="list-group-item">
                                    3) Confirm code from your phone
                                    <br/><br/>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            type="text"
                                            placeholder="XXXXXX"
                                            onChange={(e) => setOtpInput(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Button onClick={handleCheckOTPCode}>Test</Button>
                                    <span className="ps-3">{codeMatchIcon}</span>
                                </li>
                            </ul>
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="primary" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </>
    );
}

const SetupMFAModal = ({
    setLoadingStatus, hasOTPValidated, onClose
}) => {
    const [show, setShow] = useState(true);
    const [qrCodeData, setQRCodeData] = useState(true);
    const [wizardStep, setWizardStep] = useState('INIT'); // INIT | QRCODE
    // const [wizardStep, setWizardStep] = useState('QRCODE'); // INIT | QRCODE
    const [appInstalledStatus, setAppInstalledStatus] = useState(false);
  
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const handleSetupQRCode = async () => {
        setLoadingStatus(true);
        try {
            const res = await setupMyOTP();
            setWizardStep('QRCODE');
        } catch (e) {
            console.error(e);
        }
        setLoadingStatus(false);
    };
    let body = (
        <SetupMFAModalBodyAknowledge
            appInstalledStatus={appInstalledStatus}
            setAppInstalledStatus={setAppInstalledStatus}
            onClose={handleClose}
            onShow={handleShow}
            onNextStep={handleSetupQRCode}
        />
    );
    if (wizardStep == 'QRCODE') {
        body = (
            <SetupMFAModalBodyQRCode
                onClose={handleClose}
                hasOTPValidated={hasOTPValidated}
            />
        );
    }
    return (
        <Modal show={show} size="lg" onExited={onClose}>
            {body}
        </Modal>
    );
};


const RemoveMFAModal = ({
    setLoadingStatus, hasOTPValidated, onClose
}) => {
    const [show, setShow] = useState(true);
    const [isComplete, setCompletedStatus] = useState(false);
    const [otpCode, setOTPCode] = useState(false);
    const [otpCodeErrorMessage, setOTPCodeError] = useState(false);
  
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const handleOnChange = (value, completeStatus) => {
        setOTPCode(value);
        setCompletedStatus(completeStatus);
    };
    const handleRemoveOTP = async (e) => {
        setLoadingStatus(true);
        setOTPCodeError(false);
        try {
            if (e && e.target) {
                e.target.blur();
            }
            const res = await removeMyOTP({ otpCode });
            const data = res.data || {};
            if (data === true) {
                handleClose();
            } else if (data && data.message) {
                setOTPCodeError(data.message);
            }
        } catch (e) {
        }
        setLoadingStatus(false);
    }
    return (
        <Modal show={show} size="lg" onExited={onClose}>
            <Modal.Header>
                <Modal.Title>
                    Disable 2FA
                    <span className="text-muted"> - Two factor authentication</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Container fluid>
                    <Row>
                        <Col>
                            <p className="fw-bold">Enter 2FA code:</p>
                            <OTPCode onChange={handleOnChange} /><br />
                            <Button
                                disabled={!isComplete}
                                variant="danger"
                                onClick={handleRemoveOTP}>Remove 2FA from my account</Button>
                                {
                                    otpCodeErrorMessage ?
                                        <Alert variant="warning" className="mt-4">
                                            {otpCodeErrorMessage}
                                        </Alert> : null
                                }
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="link" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

const SettingsSecurity = ({
    profile
}) => {
    const navigateManager = useNavigate();
    const [ isLoading, setLoadingStatus ]  = useState(false);
    const [ tfaModalStatus, setTFAModalStatus ]  = useState(false);
    const [ removeTFAModalStatus, setRemoveTFAModalStatus ]  = useState(false);
    const [ isInit, setInitStatus ]  = useState(false);
    const [ otpStatus, setOTPStatus ] = useState(null);
    
    const handleCloseTFAModal = () => {
        setTFAModalStatus(false);
        handleRefreshOTPStatus();
    };

    const handleOpenTFAModal = (e) => {
        if (e && e.target) {
            e.target.blur();
        }
        setTFAModalStatus(true);
    }
    
    const handleCloseRemoveTFAModal = () => {
        setRemoveTFAModalStatus(false);
        handleRefreshOTPStatus();
    };

    const handleOpenRemoveTFAModal = (e) => {
        if (e && e.target) {
            e.target.blur();
        }
        setRemoveTFAModalStatus(true);
    }

    const handleRefreshOTPStatus = async () => {
        const res = await getMyOTPStatus();
        setOTPStatus(res.data);
    };

    useEffect(() => {
        if (isInit) return;
        setInitStatus(true);
        handleRefreshOTPStatus(false);
    }, [isInit])
    let otpBox = (
        <>
            <b>Status:</b> <Badge pill bg="warning" text="dark">disabled</Badge><br/>
            <div className="text-muted p-1 mb-3 mt-2">
                Your account is not protected with a second authentication system.
            </div>
            <Button variant="primary" onClick={handleOpenTFAModal}>Set up 2FA</Button>
        </>
    );
    if (otpStatus === true) {
        otpBox = (
            <>
                <b>Status:</b> <Badge pill bg="success">enabled</Badge><br/>
                <div className="text-muted p-1 mb-3 mt-2">
                    Your account is protected with 2FA.
                </div>
                <Button variant="warning" onClick={handleOpenRemoveTFAModal}>Disable 2FA</Button>
            </>
        );
    }
    return (
        <>
            { isLoading ? <Overlay show /> : null }
            { tfaModalStatus ?
                <SetupMFAModal 
                    onClose={handleCloseTFAModal}
                    setLoadingStatus={setLoadingStatus}
                    hasOTPValidated={otpStatus}
                /> : null }
            { removeTFAModalStatus ?
                <RemoveMFAModal
                    onClose={handleCloseRemoveTFAModal}
                    setLoadingStatus={setLoadingStatus}
                /> :
                null
            }
            <MainLayout pageTitle={T('SETTINGS_PAGE_SETTINGS_SECURITY')}>
                <Container className="settings-module">
                    <Row className="mt-4">
                        <Col lg={12}>
                            <Button onClick={() => navigateManager('/settings') }>{T('GEN_BACK')}</Button>
                        </Col>
                    </Row>
                    <Row className="mt-4">
                        <Col lg={4}>
                            <h3>{T('SETTINGS_P_SEC_CARD_2FA_SETUP')}</h3>
                            <Card className="mb-5 shadow">
                                <Card.Body>{otpBox}</Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </MainLayout>
        </>
    );
}

const mapStateToProps = (appState) => ({
    profile: appState.userProfile
});

const mapDispatchToProps = (dispatch) => ({
    preferencesAction: {
        setLang: (lang) => dispatch(actionCreator.Preferences.setAppLang(lang))
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsSecurity);
